import { pipe, Option, Array as A } from 'effect';

import tokenKeeper from '@/lib/apiTokenKeeper';
import { getAuthToken } from '@/services/api/auth';
import { getCompanyId } from '@/services/api/companyId';
import { eventsApi } from '@/services/api/instances';
import { buildTrailFilterParams, buildTrailScopeParams, type TrailFilters } from '@/domain/events/params';
import type { TrailSource } from '@/domain/events/schemas';

type QueryValue = string | ReadonlyArray<string> | undefined | null;

const toEntries = (params: Record<string, QueryValue>): Array<[string, string]> =>
  pipe(
    Object.entries(params),
    A.flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map((v: string): [string, string] => [key, v])
        : pipe(
            Option.fromNullable(value as string | undefined | null),
            Option.match({
              onNone: (): Array<[string, string]> => [],
              onSome: (v) => [[key, v] as [string, string]],
            }),
          ),
    ),
  );

export const buildTrailExportUrl = async (
  source: TrailSource,
  filters: TrailFilters,
  entityId?: string,
): Promise<string> => {
  // Read before the await so the export describes the company it was requested in.
  const companyId = getCompanyId();
  const dokkaToken = await getAuthToken();
  // getAuthToken resolves undefined when its refresh fails rather than rejecting,
  // and an unsigned export URL would open a guaranteed-401 tab.
  if (!dokkaToken) throw new Error('events export: no auth token');
  const query = toEntries({
    dokkaToken,
    language: tokenKeeper.getLanguage(),
    companyId,
    ...buildTrailScopeParams(entityId),
    ...buildTrailFilterParams(filters),
  });

  return `${eventsApi.getConfig().baseURL}${source}/export?${new URLSearchParams(query).toString()}`;
};
